// Home.js - Versión optimizada
import React, { Suspense, lazy, startTransition, useRef, useState, useEffect, useCallback, useMemo, memo } from "react";
import { Link } from "react-router-dom";
import "../styles/Home.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import Swal from "sweetalert2";
import { api, formatApiError, normalizeImage } from "../lib/api";
import LivePlayer from "../LivePlayer";

const Modal = lazy(() => import("react-bootstrap/Modal"));
const HOME_RATES_CACHE_KEY = "home_rates_v1";
const HOME_POPUP_CACHE_KEY = "home_popup_v1";
const HOME_CACHE_TTL_MS = 5 * 60 * 1000;

const readSessionCache = (key) => {
  try {
    const rawValue = sessionStorage.getItem(key);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (!parsed?.timestamp || Date.now() - parsed.timestamp > HOME_CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }

    return parsed.data ?? null;
  } catch {
    return null;
  }
};

const writeSessionCache = (key, data) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // No-op: si el storage falla seguimos con memoria.
  }
};

const clearSessionCache = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // No-op: si el storage falla seguimos con memoria.
  }
};

const scheduleNonCriticalTask = (callback, timeout = 1200) => {
  if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
    return {
      id: window.requestIdleCallback(callback, { timeout }),
      type: "idle",
    };
  }

  return {
    id: window.setTimeout(callback, timeout),
    type: "timeout",
  };
};

const cancelScheduledTask = (task) => {
  if (!task) return;

  if (task.type === "idle" && typeof window !== "undefined" && typeof window.cancelIdleCallback === "function") {
    window.cancelIdleCallback(task.id);
    return;
  }

  clearTimeout(task.id);
};

// Datos estáticos
const SERVICES_DATA = [
  {
    img: "img/servicio1.png",
    title: "Califica al taxista",
    text: "Espacio donde los usuarios pueden evaluar el servicio de los taxistas.",
    link: "/calf"
  },
  {
    img: "img/servicio-2.png",
    title: "Reportar objetos olvidados",
    text: "Formulario donde los usuarios puedan reportar objetos olvidados en taxis.",
    link: "/obj"
  },
  {
    img: "img/servicio-3.png",
    title: "Bolsa de Empleo",
    text: "Espacio para conectar a propietarios de taxis con conductores en busca de oportunidades laborales.",
    link: "/job"
  },
  {
    img: "img/servicio-4.png",
    title: "Club Familiar",
    text: "Para consolidar la integración gremial y familiar, realizamos eventos con este objetivo y lo haremos con más frecuencia.",
    link: "/drivep"
  }
];

const EMPRESAS_DATA = [
  [
    { img: "img/valcali.webp", name: "Valcali", url:"https://sindiunion.com/"},
    { img: "img/toro-autos.webp", name: "Toro Autos", url:"https://toroautos.com/" },
    { img: "img/taxis-y-autos-cali.webp", name: "Taxis y Autos Cali", url:"https://cali.taxexpress.com.co/" },
    { img: "img/Logo-Tx-plus.webp", name: "TX Plus", url:"https://app.txplus.com.co/  " }
  ],
  [
    { img: "img/asotaba.webp", name: "Asotaba", url:"https://asotaba.com/" },
    { img: "img/servitaxis.webp", name: "ServiTaxis", url:"https://servitaxis.co/" },
    { img: "img/radio-taxi-california.webp", name: "Radio Taxi California", url:"" },
    { img: "img/logo-taxis-libres.webp", name: "Taxi Libres", url:"https://www.taxislibres.com.co/taxis-cali" }
  ]
];

const QUIENES_SOMOS_DATA = [
  {
    id: "qs-s1",
    img: "img/mision-1.png",
    title: "Misión",
    text: "Ser una plataforma pedagógica y sostenible, diseñada para dignificar el transporte individual tipo taxi, promoviendo un servicio eficiente, seguro y amable. Nuestro enfoque social y cívico busca impactar positivamente en la calidad de vida de conductores y usuarios, contribuyendo al desarrollo social y económico de la región."
  },
  {
    id: "qs-s2",
    img: "img/vision-1.png",
    title: "Visión",
    text: "Posicionarnos como la principal plataforma tecnológica cívico-social en el sector de transporte público individual en Colombia. Aspiramos a liderar la transformación del servicio de taxi, garantizando excelencia, seguridad y satisfacción para conductores y usuarios, siendo reconocidos como un modelo de innovación y calidad a nivel nacional."
  },
  {
    id: "qs-s3",
    img: "img/valores-1.png",
    title: "Valores",
    bullets: [
      ["Compromiso", "Trabajamos con dedicación para brindar un servicio confiable y eficiente."],
      ["Innovación", "Incorporamos herramientas tecnológicas que mejoran la experiencia del usuario y del conductor."],
      ["Responsabilidad social", "Fomentamos el respeto y el buen trato entre la comunidad y los conductores."],
      ["Calidad", "Nos enfocamos en garantizar altos estándares en todos los aspectos del servicio."],
      ["Seguridad", "Priorizamos la tranquilidad de los usuarios y conductores en cada interacción."],
    ]
  }
];

const FALLBACK_TESTIMONIOS = [
  {
    id: 9,
    driverId: 1,
    taxiPlate: "ABC123",
    name: "Jonathan",
    email: "jonathan@jonathan.com",
    contact: "312688773",
    observation: "Buen conductor mal carril",
    rate: "4",
    createdAt: "2025-10-08T01:55:38.000Z",
    updatedAt: "2025-10-08T01:55:38.000Z",
    deletedAt: null
  },
  {
    id: 10,
    driverId: 2,
    taxiPlate: "XYZ987",
    name: "María",
    email: "maria@maria.com",
    contact: "310555999",
    observation: "Servicio puntual, buena vibra",
    rate: "5",
    createdAt: "2025-10-09T11:22:10.000Z",
    updatedAt: "2025-10-09T11:22:10.000Z",
    deletedAt: null
  },
  {
    id: 11,
    driverId: 3,
    taxiPlate: "JKL456",
    name: "Carlos",
    email: "carlos@carlos.com",
    contact: "300112233",
    observation: "Conductor amable, taxi limpio",
    rate: "5",
    createdAt: "2025-10-10T07:40:55.000Z",
    updatedAt: "2025-10-10T07:40:55.000Z",
    deletedAt: null
  }
];

// Componentes memoizados
const ServiceCard = memo(({ img, title, text, link }) => (
  <Link to={link} className="text-decoration-none text-dark" style={{ transition: "transform 0.3s" }}>
    <div className="card servicio-card h-100 shadow-sm hover-shadow">
      <img src={img} className="card-img-top" alt={title} loading="lazy" decoding="async" />
      <div className="card-body">
        <h5 className="card-title">{title}</h5>
        <p className="card-text">{text}</p>
      </div>
    </div>
  </Link>
));

ServiceCard.displayName = "ServiceCard";

const EmpresaCard = memo(({ img, name, url }) => {
  const safeUrl = url && String(url).trim() ? String(url).trim() : null;
  const content = (
    <div className="empresa-card">
      <img src={img} alt={name} loading="lazy" decoding="async" />
      <p>{name}</p>
    </div>
  );

  if (safeUrl) {
    return (
      <a href={safeUrl} target="_blank" rel="noopener noreferrer" className="empresa-link">
        {content}
      </a>
    );
  }

  return content;
});

EmpresaCard.displayName = "EmpresaCard";

const QuienesSlide = memo(({ id, img, title, text, bullets, nextId }) => (
  <article className={`slide ${id.replace("qs-", "")}`}>
    <div className="left">
      <img src={img} alt={`${title} — La Mancha Amarilla`} loading="lazy" decoding="async" />
      <label className="click-area" htmlFor={nextId}></label>
    </div>
    <div className="right">
      <h2>{title}</h2>
      {bullets ? (
        <div>
          {bullets.map(([label, value]) => (
            <p key={label}>
              <strong>{label}:</strong> {value}
            </p>
          ))}
        </div>
      ) : (
        <p>{text}</p>
      )}
      <label className="click-area-text" htmlFor={nextId}></label>
    </div>
  </article>
));

QuienesSlide.displayName = "QuienesSlide";

const TestimonialCard = memo(({ observation, rate }) => (
  <div className="testimonial-card">
    <div className="quote">"</div>
    <p>{observation}</p>
    <div className="stars">{"★".repeat(Number(rate))}</div>
  </div>
));

TestimonialCard.displayName = "TestimonialCard";

const ImagePopup = memo(({ show, image, onClose }) => (
  !show || !image?.url ? null : (
    <Suspense fallback={null}>
      <Modal show={show} onHide={onClose} centered size="xl" backdrop>
        <Modal.Body className="p-0" style={{ background: "transparent" }}>
          <div style={{ position: "relative" }}>
            <button
              aria-label="Cerrar"
              onClick={onClose}
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                background: "rgba(0,0,0,0.45)",
                border: "none",
                color: "#fff",
                padding: "6px 10px",
                borderRadius: "20px",
                zIndex: 20,
                cursor: "pointer",
                fontSize: "1.1rem",
                lineHeight: 1,
              }}
            >
              ×
            </button>
            <img
              src={image.url}
              alt={image.filename}
              className="w-100"
              decoding="async"
              onError={onClose}
              style={{ display: "block", maxHeight: "80vh", width: "100%", objectFit: "cover" }}
            />
          </div>
        </Modal.Body>
      </Modal>
    </Suspense>
  )
));

ImagePopup.displayName = "ImagePopup";

// Componente principal
export default function Home() {
  const [rates, setRates] = useState(() => readSessionCache(HOME_RATES_CACHE_KEY) || []);
  const [showPopup, setShowPopup] = useState(false);
  const [popupImage, setPopupImage] = useState(() => readSessionCache(HOME_POPUP_CACHE_KEY));
  const [loading, setLoading] = useState(() => ({
    rates: !readSessionCache(HOME_RATES_CACHE_KEY)?.length,
    image: !readSessionCache(HOME_POPUP_CACHE_KEY),
  }));

  // Obtener datos
  const getPopupImage = useCallback(async () => {
    try {
      const res = await api.get("/images");
      const payload = res?.data;
      if (payload?.code === 1 && payload.data) {
        const normalizedImage = normalizeImage(payload.data);
        if (normalizedImage?.url) {
          writeSessionCache(HOME_POPUP_CACHE_KEY, normalizedImage);
          startTransition(() => {
            setPopupImage(normalizedImage);
            setShowPopup(true);
          });
          return;
        }
      }

      clearSessionCache(HOME_POPUP_CACHE_KEY);
      startTransition(() => {
        setPopupImage(null);
        setShowPopup(false);
      });
    } catch (err) {
      clearSessionCache(HOME_POPUP_CACHE_KEY);
      startTransition(() => {
        setPopupImage(null);
        setShowPopup(false);
      });
    } finally {
      setLoading(prev => ({ ...prev, image: false }));
    }
  }, []);

  const getRates = useCallback(async () => {
    try {
      const res = await api.get("/rate_driver/top");
      const payload = res?.data;
      if (payload?.code === 1) {
        writeSessionCache(HOME_RATES_CACHE_KEY, payload.data);
        startTransition(() => {
          setRates(payload.data);
        });
      } else {
        startTransition(() => {
          setRates(FALLBACK_TESTIMONIOS);
        });
      }
    } catch (error) {
      startTransition(() => {
        setRates(FALLBACK_TESTIMONIOS);
      });
    } finally {
      setLoading(prev => ({ ...prev, rates: false }));
    }
  }, []);


  // Inicialización
  useEffect(() => {
    let isMounted = true;
    const scheduledTasks = [];

    const init = async () => {
      await getRates();

      if (!isMounted) return;

      scheduledTasks.push(
        scheduleNonCriticalTask(() => {
          if (!isMounted) return;
          getPopupImage();
        }, 1200)
      );

    };

    init();

    return () => {
      isMounted = false;
      scheduledTasks.forEach(cancelScheduledTask);
    };
  }, [ getPopupImage, getRates]);

  useEffect(() => {
    if (!showPopup) return undefined;

    const timer = setTimeout(() => {
      setShowPopup(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, [showPopup]);


  // Testimonios para mostrar (memoizado)
  const displayedTestimonios = useMemo(() => 
    rates.slice(0, 3), 
    [rates]
  );

  return (
    <div className="home-page">
      {/* HERO */}
      <section id="Hero" className="hero position-relative">
        <video autoPlay muted loop playsInline className="hero-video" preload="metadata">
          <source src="play/hero-2.mp4" type="video/mp4" />
          Tu navegador no soporta videos HTML5.
        </video>
        <div className="hero-overlay d-flex flex-column justify-content-center align-items-center text-center">
          <h1>Bienvenido a La Mancha Amarilla</h1>
          <p>Transformando el transporte individual con innovación, seguridad y compromiso social.</p>
          <Link to="/drivep" className="btn-hero mt-3">Registrarse</Link>
        </div>
      </section>

      {/* EN VIVO */}
      <section id="en-vivo" className="live-section py-5">
        <div className="container">
          <div className="text-center mb-4">
            <h2 className="section-title">En vivo</h2>
            <p className="live-section__subtitle">Noticias del gremio de lunes a viernes a las 6:00 p. m.</p>
          </div>
          <LivePlayer />
          <div className="text-center mt-4">
            <Link to="/envivo" className="btn-hero">Ir a la página En vivo</Link>
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="py-5">
        <div className="container text-center">
          <h2 className="section-title mb-5">Nuestros Servicios</h2>
          <div className="row g-4">
            {SERVICES_DATA.map((servicio, i) => (
              <div key={i} className="col-12 col-md-6 col-lg-3">
                <ServiceCard {...servicio} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EMPRESAS */}
      <section id="empresas" className="py-5">
        <div className="container text-center">
          <h2 className="section-title mb-5">EMPRESAS QUE TRABAJAN CON NOSOTROS</h2>
          <div id="empresasCarousel" className="carousel slide" data-bs-ride="carousel" data-bs-interval="6000">
            <div className="carousel-inner">
              {EMPRESAS_DATA.map((grupo, idx) => (
                <div key={idx} className={`carousel-item ${idx === 0 ? 'active' : ''}`}>
                  <div className="row g-4 justify-content-center">
                    {grupo.map((empresa, i) => (
                      <div key={i} className="col-6 col-md-3">
                        <EmpresaCard {...empresa} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-control-prev" type="button" data-bs-target="#empresasCarousel" data-bs-slide="prev">
              <span className="carousel-control-prev-icon" aria-hidden="true"></span>
              <span className="visually-hidden">Previous</span>
            </button>
            <button className="carousel-control-next" type="button" data-bs-target="#empresasCarousel" data-bs-slide="next">
              <span className="carousel-control-next-icon" aria-hidden="true"></span>
              <span className="visually-hidden">Next</span>
            </button>
          </div>
        </div>
      </section>

      {/* QUIENES SOMOS */}
      <section id="Sobre-Nosotros" className="quienes-section" aria-label="Quienes somos - Misión, Visión, Valores">
        <div className="quienes-slider">
          {QUIENES_SOMOS_DATA.map((item, idx) => (
            <input key={item.id} type="radio" name="qs-slide" id={item.id} defaultChecked={idx === 0} />
          ))}
          
          <div className="slides">
            {QUIENES_SOMOS_DATA.map((item, idx) => (
              <QuienesSlide
                key={item.id}
                {...item}
                nextId={QUIENES_SOMOS_DATA[(idx + 1) % QUIENES_SOMOS_DATA.length].id}
              />
            ))}
          </div>
          
          <div className="qs-dots" role="tablist" aria-label="Navegación Quiénes somos">
            {QUIENES_SOMOS_DATA.map((item) => (
              <label key={item.id} htmlFor={item.id} className="dot" aria-label={item.title} role="tab"></label>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section className="testimonials">
        <div className="container">
          <h2 className="section-title">Lo que dicen nuestros usuarios</h2>
          <p className="section-subtitle">Conoce la experiencia de quienes usan nuestros servicios de taxi</p>
          
          {loading.rates ? (
            <div className="text-center py-5">
              <div className="spinner-border text-warning" role="status">
                <span className="visually-hidden">Cargando testimonios...</span>
              </div>
            </div>
          ) : (
            <div className="testimonials-grid">
              {displayedTestimonios.map((t) => (
                <TestimonialCard key={t.id} observation={t.observation} rate={t.rate} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ÚNETE */}
      <section id="contacto" className="unete-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="fw-bold mb-3">Únete a La Mancha Amarilla</h2>
              <p>Si eres taxista, regístrate en nuestro parche en lamanchaamarilla.com y sé parte del cambio.</p>
              <p>Nuestro programa es transmitido desde Cali de lunes a viernes de 6 a 8 pm.</p>
              <p>Invitamos a gremios de otras ciudades para transmitir en la emisora, contáctanos para más detalles.</p>
              <Link to="/drivep" className="btn btn-warning fw-bold">Registrarse</Link>
            </div>
            <div className="col-lg-6 text-center">
              <img src="img/unete.png" alt="Taxis La Mancha Amarilla" className="img-fluid rounded shadow" loading="lazy" decoding="async" />
            </div>
          </div>
        </div>
      </section>

      {/* POPUP */}
      <ImagePopup 
        show={showPopup} 
        image={popupImage} 
        onClose={() => setShowPopup(false)} 
      />
    </div>
  );
}
